import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Ban, Activity, Users, Lock, Unlock } from 'lucide-react';
import { secureApi, tokenStorage, hasPermission } from '../utils/auth';
import SecurityAlert from '../components/SecurityAlert';

const AdminSecurityDashboard = () => {
  const [securityEvents, setSecurityEvents] = useState([]);
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [stats, setStats] = useState({
    totalEvents: 0,
    failedLogins: 0,
    blockedIPs: 0,
    suspiciousActivity: 0
  });
  const [timeRange, setTimeRange] = useState(24);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [showBlockIP, setShowBlockIP] = useState(false);
  const [blockIPForm, setBlockIPForm] = useState({
    ip_address: '',
    reason: '',
    duration_hours: 24
  });

  const user = tokenStorage.getUser();

  useEffect(() => {
    if (!hasPermission('admin')) {
      window.location.href = '/dashboard';
      return;
    }
    loadSecurityData();
  }, [timeRange]);

  const loadSecurityData = async () => {
    setLoading(true);
    try {
      // Load security events
      const eventsData = await secureApi.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/security-events?hours=${timeRange}`
      );
      setSecurityEvents(eventsData.events || []);

      // Load blocked IPs
      const blockedData = await secureApi.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/blocked-ips`
      );
      setBlockedIPs(blockedData.blocked_ips || []);

      // Load recent audit log
      const auditData = await secureApi.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/audit-log?limit=50`
      );
      setAuditLog(auditData.logs || []);

      // Calculate stats
      const failed = eventsData.events.filter(e => e.action === 'user_login_failed').length;
      const suspicious = eventsData.events.filter(e => e.action === 'suspicious_activity').length;
      
      setStats({
        totalEvents: eventsData.events.length,
        failedLogins: failed,
        blockedIPs: blockedData.blocked_ips.length,
        suspiciousActivity: suspicious
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load security data' });
    } finally {
      setLoading(false);
    }
  };

  const handleBlockIP = async (e) => {
    e.preventDefault();
    try {
      await secureApi.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/block-ip`,
        blockIPForm
      );
      
      setMessage({ type: 'success', text: `IP ${blockIPForm.ip_address} blocked successfully` });
      setShowBlockIP(false);
      setBlockIPForm({ ip_address: '', reason: '', duration_hours: 24 });
      loadSecurityData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to block IP' });
    }
  };

  const handleUnblockIP = async (ip) => {
    if (!window.confirm(`Unblock IP ${ip}?`)) return;

    try {
      await secureApi.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/block-ip/${ip}`
      );
      
      setMessage({ type: 'success', text: `IP ${ip} unblocked successfully` });
      loadSecurityData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to unblock IP' });
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const getSeverityColor = (severity) => {
    const colors = {
      info: 'text-blue-600 bg-blue-50',
      warning: 'text-yellow-600 bg-yellow-50',
      error: 'text-red-600 bg-red-50',
      critical: 'text-purple-600 bg-purple-50'
    };
    return colors[severity] || colors.info;
  };

  if (!hasPermission('admin')) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Security Dashboard</h1>
                <p className="text-gray-600">Monitor and manage system security</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={1}>Last 1 hour</option>
                <option value={6}>Last 6 hours</option>
                <option value={24}>Last 24 hours</option>
                <option value={168}>Last 7 days</option>
              </select>
              
              <button
                onClick={loadSecurityData}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <SecurityAlert
            type={message.type}
            message={message.text}
            onDismiss={() => setMessage(null)}
          />
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Events</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalEvents}</p>
              </div>
              <Activity className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Failed Logins</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{stats.failedLogins}</p>
              </div>
              <Lock className="h-8 w-8 text-red-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Blocked IPs</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">{stats.blockedIPs}</p>
              </div>
              <Ban className="h-8 w-8 text-orange-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Suspicious Activity</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">{stats.suspiciousActivity}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Blocked IPs */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Ban className="h-6 w-6 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Blocked IP Addresses</h2>
            </div>
            
            <button
              onClick={() => setShowBlockIP(!showBlockIP)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
            >
              Block IP
            </button>
          </div>

          {showBlockIP && (
            <form onSubmit={handleBlockIP} className="mb-4 p-4 bg-gray-50 rounded-lg space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IP Address</label>
                <input
                  type="text"
                  value={blockIPForm.ip_address}
                  onChange={(e) => setBlockIPForm({ ...blockIPForm, ip_address: e.target.value })}
                  placeholder="192.168.1.100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <input
                  type="text"
                  value={blockIPForm.reason}
                  onChange={(e) => setBlockIPForm({ ...blockIPForm, reason: e.target.value })}
                  placeholder="Suspicious activity detected"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (hours)</label>
                <input
                  type="number"
                  value={blockIPForm.duration_hours}
                  onChange={(e) => setBlockIPForm({ ...blockIPForm, duration_hours: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="1"
                />
              </div>
              
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Block IP
                </button>
                <button
                  type="button"
                  onClick={() => setShowBlockIP(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="space-y-2">
            {blockedIPs.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No blocked IPs</p>
            ) : (
              blockedIPs.map((block, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{block.ip_address}</p>
                    <p className="text-sm text-gray-600">{block.reason}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Blocked: {formatDate(block.blocked_at)}
                      {block.expires_at && ` • Expires: ${formatDate(block.expires_at)}`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleUnblockIP(block.ip_address)}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  >
                    <Unlock className="h-4 w-4" />
                    Unblock
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Security Events */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="h-6 w-6 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Recent Security Events</h2>
          </div>

          <div className="space-y-2">
            {securityEvents.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No security events</p>
            ) : (
              securityEvents.slice(0, 20).map((event, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityColor(event.severity)}`}>
                    {event.severity || 'info'}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{event.description || event.action}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {formatDate(event.timestamp)}
                      {event.ip_address && ` • ${event.ip_address}`}
                      {event.user_id && event.user_id !== 'anonymous' && ` • User: ${event.user_id.substring(0, 8)}`}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Audit Log */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <Users className="h-6 w-6 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Audit Log</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Time</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">User</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Action</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">IP Address</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {auditLog.slice(0, 30).map((log, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-gray-900">
                      {log.user_id === 'anonymous' ? 'Anonymous' : log.user_id.substring(0, 8)}
                    </td>
                    <td className="px-4 py-3 text-gray-900">{log.action}</td>
                    <td className="px-4 py-3 text-gray-600">{log.ip_address || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {log.resource_type && `${log.resource_type}: ${log.resource_id?.substring(0, 8) || ''}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSecurityDashboard;
